import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import Arrow from '@assets/images/dashboard/pageArrow.svg';
import Arrow2 from '@assets/images/dashboard/pageArrow2.svg';
import useWindow from '@lib/hooks/useWindow';
import styled from 'styled-components';

interface PaginationProps {
  setCurrentPage: Dispatch<SetStateAction<number>>;
  currentPage: number;
  totalcount: number;
  postsPerPage: number;
  fetchData?: (currentPage: number, postsPerPage: number) => void;
  padding?: number;
}

const TablePagination: React.FunctionComponent<PaginationProps> = ({
  setCurrentPage,
  totalcount,
  postsPerPage,
  currentPage,
  fetchData,
  padding,
}) => {
  const windowInfo = useWindow();
  const isMobile = windowInfo?.width ? windowInfo.width < 768 : false;
  const pageUnit = isMobile ? 5 : 10;

  const totalPages = Math.max(1, Math.ceil(totalcount / postsPerPage));
  const arrayNum =
    currentPage % pageUnit === 0
      ? Math.floor(currentPage / pageUnit) - 1
      : Math.floor(currentPage / pageUnit);

  const goToPage = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      fetchData?.(newPage, postsPerPage);
    },
    [setCurrentPage, fetchData, postsPerPage],
  );

  const handleFirstPage = useCallback(() => {
    const newPage = currentPage - pageUnit > 0 ? currentPage - pageUnit : 1;
    goToPage(newPage);
  }, [currentPage, pageUnit, goToPage]);

  const handlePrevPage = useCallback(() => {
    const newPage = currentPage - 1 === 0 ? 1 : currentPage - 1;
    goToPage(newPage);
  }, [currentPage, goToPage]);

  const handleNextPage = useCallback(() => {
    const newPage = currentPage === totalPages ? totalPages : currentPage + 1;
    goToPage(newPage);
  }, [currentPage, totalPages, goToPage]);

  const handleLastPage = useCallback(() => {
    const newPage = currentPage + pageUnit < totalPages ? currentPage + pageUnit : totalPages;
    goToPage(newPage);
  }, [currentPage, pageUnit, totalPages, goToPage]);

  const visiblePages = useMemo(() => {
    const items = Array.from({ length: totalPages }, (_, i) => {
      const pageNumber = i + 1;
      const isActive = pageNumber === currentPage;
      return (
        <PageButton key={pageNumber} $isActive={isActive} onClick={() => goToPage(pageNumber)}>
          {pageNumber}
        </PageButton>
      );
    });

    if (items.length <= pageUnit) {
      return items;
    }

    const chunked: React.ReactNode[][] = [];
    for (let i = 0; i < items.length; i += pageUnit) {
      chunked.push(items.slice(i, i + pageUnit));
    }
    return chunked[arrayNum];
  }, [totalPages, currentPage, pageUnit, arrayNum, goToPage]);

  return (
    <PaginationContainer $padding={padding}>
      <PaginationWrapper aria-label="페이지 이동">
        <ArrowButton onClick={handleFirstPage}>
          <img src={Arrow2} alt="«" style={{ transform: 'rotate(180deg)' }} />
        </ArrowButton>
        <ArrowButton onClick={handlePrevPage}>
          <img src={Arrow} alt="‹" style={{ transform: 'rotate(180deg)' }} />
        </ArrowButton>
        {visiblePages}
        <ArrowButton onClick={handleNextPage}>
          <img src={Arrow} alt="›" />
        </ArrowButton>
        <ArrowButton onClick={handleLastPage}>
          <img src={Arrow2} alt="»" />
        </ArrowButton>
      </PaginationWrapper>
    </PaginationContainer>
  );
};

export default TablePagination;

const PaginationContainer = styled.div<{ $padding?: number }>`
  display: flex;
  justify-content: center;
  padding: ${({ $padding }) => ($padding === 0 ? '0.5rem 0' : '2rem 0 2rem')};
`;

const PaginationWrapper = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PageButton = styled.button<{ $isActive: boolean }>`
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;

  background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.primary : theme.neutralGray[300]};
  color: ${({ theme }) => theme.white};

  &:hover {
    background-color: ${({ theme, $isActive }) => ($isActive ? theme.primary : theme.main[200])};
  }

  &:focus {
    outline: none;
  }
`;

const ArrowButton = styled.button`
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.25rem;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    background-color: ${({ theme }) => theme.white};
  }

  &:focus {
    outline: none;
  }

  img {
    height: 0.75rem;
    width: auto;
  }
`;
